import {Button, CreateIcon, Input, ModalPopup} from '@kit';

import React, {useCallback, useState} from 'react';
import {useApolloClient} from '@apollo/client/react';

import {ImageTarget, useAddEntityImageMutation} from '../../generated/types';

import css from './gallery.module.css';

export type GalleryTarget = {
	type: ImageTarget;
	id: string;
};

type UploadAnswer = {path?: string; width?: number; height?: number; error?: string};

async function uploadFile(target: GalleryTarget, file: File): Promise<Required<Omit<UploadAnswer, 'error'>>> {
	const response = await fetch(`/api/gallery-image?type=${target.type}&id=${encodeURIComponent(target.id)}`, {
		method: 'POST',
		body: file,
	});
	const answer = (await response.json().catch(() => ({}))) as UploadAnswer;
	if (!response.ok || !answer.path || !answer.width || !answer.height) {
		throw new Error(answer.error || 'Не удалось загрузить картинку');
	}
	return {path: answer.path, width: answer.width, height: answer.height};
}

type Props = {
	target: GalleryTarget;
};

export const GalleryUpload: React.FC<Props> = ({target}) => {
	const client = useApolloClient();
	const [addImage] = useAddEntityImageMutation();

	const [open, setOpen] = useState(false);
	const [file, setFile] = useState<File>();
	const [credits, setCredits] = useState('');
	const [description, setDescription] = useState('');
	const [error, setError] = useState<string>();
	const [busy, setBusy] = useState(false);

	const close = useCallback(() => {
		setOpen(false);
		setFile(undefined);
		setCredits('');
		setDescription('');
		setError(undefined);
	}, []);

	const submit = useCallback(
		async (event: React.FormEvent) => {
			event.preventDefault();
			if (!file || busy) return;
			setBusy(true);
			setError(undefined);
			try {
				const uploaded = await uploadFile(target, file);
				const result = await addImage({
					variables: {
						target: target.type,
						targetId: target.id,
						...uploaded,
						credits: credits.trim() || undefined,
						description: description.trim() || undefined,
					},
				});
				if (result.error) throw result.error;
				await client.refetchQueries({include: 'active'});
				close();
			} catch (failure) {
				setError(failure instanceof Error ? failure.message : 'Не удалось добавить картинку');
			} finally {
				setBusy(false);
			}
		},
		[file, busy, target, addImage, credits, description, client, close],
	);

	return (
		<div className={css.upload}>
			<Button appearance="subtle" size="small" icon={<CreateIcon />} onClick={() => setOpen(true)}>
				Добавить картинку
			</Button>
			<ModalPopup open={open} onClose={close} header="Добавить картинку в галерею" maxWidth={520}>
				<form className={css.uploadForm} onSubmit={submit}>
					<input
						type="file"
						accept="image/*"
						onChange={(event) => setFile(event.target.files?.[0])}
						aria-label="Файл картинки"
					/>
					<Input label="Автор" value={credits} onChange={setCredits} />
					<Input label="Подпись" value={description} onChange={setDescription} />
					{error && <div className={css.uploadError}>{error}</div>}
					<div className={css.uploadButtons}>
						<Button type="submit" appearance="primary" loading={busy} disabled={!file}>
							Загрузить
						</Button>
						<Button onClick={close}>Отмена</Button>
					</div>
				</form>
			</ModalPopup>
		</div>
	);
};
